// Окно команд и поиска ⌘K (components/CommandPalette/README.md) на cmdk и Radix Dialog:
// по центру сверху, строка ввода «иконка режима → поле → Esc», группы с заголовками,
// совпадение выделено цветом акцента, подвал «↑↓ выбрать · ↵ открыть · Esc закрыть».
// Режимы «команды · поиск» (ux.md, раздел 8): Tab переключает, Backspace в пустом поле
// возвращает к первому режиму; в подвале — «Tab поиск». Последняя строка с вводом —
// «Все результаты по «…»», переход на страницу поиска. Пункт с keepOpen палитру не закрывает:
// «Повторить поиск» после сбоя повторяет запрос, ввод и режим остаются; пока запрос идёт, у пункта
// спиннер на месте иконки (loading).
// Что показывать, решает потребитель: он передаёт группы уже отобранными по вводу и режиму.
// Пустая группа скрыта, если у неё нет строки-пояснения: «Недавние» при пустом вводе видны
// всегда (FR-037а). «Поиск…» — только если поиск идёт дольше 0,3 с (FR-040а). Пункт, выбранный
// клавишами, обведён, как фокус; под указателем — только фон (useListNav, README «Состояния»).
// 044: палитра уходит за 160 мс (kp-pal-out, затемнение — kp-fade-out); выбранный пункт
// выполняется в начале выхода, а не после него (MOT-08, research Р6).
import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import { Icon } from '../lib/icon.tsx';
import type { LucideIcon } from '../lib/icon.tsx';
import { relayEscape } from '../lib/layers.ts';
import { useListNav } from '../lib/list-nav.ts';
import { useReturnFocus } from '../lib/return-focus.ts';
import { ToastLayerRegion } from '../lib/toast-layer.tsx';
import { useDelayedFlag } from '../lib/use-delayed.ts';
import { Kbd, ShortcutKeys } from './Kbd.tsx';
import type { Shortcut } from './Kbd.tsx';

export interface CommandPaletteItem {
  /** Ключ пункта, уникальный в палитре. */
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Пояснение серым после названия: почта пользователя, раздел. */
  sub?: string;
  /** Справа: клавиша команды («N», «G затем H») или ключ записи. */
  shortcut?: Shortcut;
  /**
   * Выбор пункта: палитра закрывается, фокус возвращается на место, затем выполняется
   * действие — переход или окно получают фокус уже после палитры. Всё это — в начале выхода
   * палитры, без ожидания её ухода (044, MOT-08).
   */
  onSelect: () => void;
  /**
   * Пункт не закрывает палитру: действие выполняется сразу, ввод, режим и фокус в поле остаются.
   * Так «Повторить поиск» после сбоя повторяет запрос на месте.
   */
  keepOpen?: boolean;
  /**
   * Действие пункта идёт: спиннер на месте иконки того же размера, пункт занят (aria-busy),
   * повторный выбор не срабатывает. Так «Повторить поиск» показывает, что запрос ушёл (T168).
   */
  loading?: boolean;
}

export interface CommandPaletteGroup {
  id: string;
  /** Заголовок группы: «Действия», «Недавние», «Перейти». */
  heading: string;
  items: readonly CommandPaletteItem[];
  /**
   * Строка-пояснение, пока пунктов нет: группа остаётся видна («Недавние» при пустом вводе).
   * Пояснение — неактивный пункт: его не выбрать ни стрелками, ни мышью.
   */
  emptyText?: string;
}

/** Режим строки ввода: «Команды» или «Поиск» (ux.md, раздел 8). */
export interface CommandPaletteMode {
  id: string;
  /** «Команды», «Поиск»: в подвале — «Tab поиск», для программ чтения — «Режим: поиск». */
  label: string;
  /** Иконка режима в начале строки ввода. */
  icon: LucideIcon;
  placeholder: string;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Имя окна и поля для программ чтения экрана: «Найти или перейти». */
  label: string;
  placeholder?: string;
  query: string;
  onQueryChange: (query: string) => void;
  groups: readonly CommandPaletteGroup[];
  /** Выделяется в названиях пунктов. */
  highlight?: string;
  /** Идёт поиск на сервере; «Поиск…» появляется, только если ожидание дольше 0,3 с. */
  loading?: boolean;
  /** Ничего не найдено: «Ничего не найдено по «…»» и подсказка. */
  empty?: ReactNode;
  /**
   * Режимы по порядку: первый — по умолчанию («Команды»). Tab — следующий режим, Shift+Tab —
   * предыдущий, Backspace в пустом поле — первый. Один режим или ни одного — Tab не переключает.
   */
  modes?: readonly CommandPaletteMode[];
  /** Текущий режим — id из modes; управляет потребитель. */
  mode?: string;
  onModeChange?: (mode: string) => void;
  /** Последняя строка: «Все результаты по «…»» — переход на страницу поиска. */
  allResults?: CommandPaletteItem;
  /** Куда вернуть фокус после закрытия; по умолчанию — откуда открыли. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export interface HighlightPart {
  text: string;
  match: boolean;
}

/** «Поиск» → «поиск»: название режима внутри фразы. */
function lower(text: string): string {
  return text.toLocaleLowerCase('ru-RU');
}

/** Ё и е, регистр — не различаются: «елк» находит «Ёлкин». */
function fold(text: string): string {
  return text.toLocaleLowerCase('ru-RU').replaceAll('ё', 'е');
}

/** Части названия с первым совпадением ввода — для выделения цветом акцента. */
export function highlightParts(text: string, query: string): HighlightPart[] {
  const needle = fold(query.trim());
  const at = needle ? fold(text).indexOf(needle) : -1;
  if (at < 0) return [{ text, match: false }];
  const parts: HighlightPart[] = [];
  if (at > 0) parts.push({ text: text.slice(0, at), match: false });
  parts.push({ text: text.slice(at, at + needle.length), match: true });
  if (at + needle.length < text.length) {
    parts.push({ text: text.slice(at + needle.length), match: false });
  }
  return parts;
}

function Highlighted({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightParts(text, query).map((part, index) =>
        part.match ? <mark key={index}>{part.text}</mark> : <span key={index}>{part.text}</span>,
      )}
    </>
  );
}

export function CommandPalette({
  open,
  onOpenChange,
  label,
  placeholder = 'Введите команду или найдите…',
  query,
  onQueryChange,
  groups,
  highlight = '',
  loading = false,
  empty,
  modes = [],
  mode,
  onModeChange,
  allResults,
  returnFocusRef,
}: CommandPaletteProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const returnFocus = useReturnFocus(open, contentRef, { returnFocusRef });
  const visible = groups.filter((group) => group.items.length > 0 || Boolean(group.emptyText));
  const searching = useDelayedFlag(loading);
  const listNav = useListNav(open);
  const pending = useRef<(() => void) | null>(null);
  const modeHintId = useId();

  const modeIndex = Math.max(
    0,
    modes.findIndex((item) => item.id === mode),
  );
  const current = modes[modeIndex];
  const switchable = modes.length > 1 && onModeChange !== undefined;
  const next = switchable ? modes[(modeIndex + 1) % modes.length] : undefined;

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!switchable || event.nativeEvent.isComposing) return;
    if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
      // Tab в палитре не уводит фокус из поля (в окне оно одно), а меняет режим.
      event.preventDefault();
      const step = event.shiftKey ? modes.length - 1 : 1;
      const target = modes[(modeIndex + step) % modes.length];
      if (target) onModeChange(target.id);
      return;
    }
    if (event.key === 'Backspace' && query === '' && modeIndex > 0) {
      event.preventDefault();
      const first = modes[0];
      if (first) onModeChange(first.id);
    }
  };

  const select = (item: CommandPaletteItem) => {
    if (item.keepOpen) {
      // Мышью фокус мог уйти с поля: ввод продолжается с того же места.
      inputRef.current?.focus();
      if (!item.loading) item.onSelect();
      return;
    }
    if (item.loading) return;
    pending.current = item.onSelect;
    onOpenChange(false);
  };

  // Начало выхода: фокус уже вернулся на место (эффект useReturnFocus объявлен выше и идёт раньше),
  // пункт выполняется сразу — окно, которое он открывает, запоминает опору палитры, а не её поле.
  useEffect(() => {
    if (open) return;
    const run = pending.current;
    pending.current = null;
    run?.();
  }, [open]);

  const afterClose = (event: Event) => {
    returnFocus(event);
    // Палитру сняли со страницы без закрытия (потребитель убрал её) — пункт выполняется здесь.
    const run = pending.current;
    pending.current = null;
    run?.();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="kp-overlay kp-pal-overlay">
          <Dialog.Content
            ref={contentRef}
            className="t-pal kp-pal"
            data-nav={listNav.nav}
            aria-describedby={undefined}
            onCloseAutoFocus={afterClose}
            // Esc, пока палитра уходит, — следующему слою (044, MOT-07).
            onEscapeKeyDown={(event) => {
              if (open) return;
              event.preventDefault();
              relayEscape(contentRef.current, event);
            }}
          >
            <Dialog.Title className="kp-sr-only">{label}</Dialog.Title>
            {/* Отбор пунктов — у потребителя: разделы по первым буквам, поиск на сервере.
                Ctrl+K в cmdk двигает выбор вверх — отключено: ⌘K открывает и закрывает палитру. */}
            <Command
              label={label}
              shouldFilter={false}
              loop
              vimBindings={false}
              onKeyDown={listNav.onKeyDown}
            >
              <div className="t-pal-in">
                <Icon icon={current?.icon ?? Search} />
                <Command.Input
                  ref={inputRef}
                  className="kp-pal-input"
                  value={query}
                  onValueChange={onQueryChange}
                  placeholder={current?.placeholder ?? placeholder}
                  onKeyDown={onInputKeyDown}
                  aria-describedby={current ? modeHintId : undefined}
                />
                {current ? (
                  <span id={modeHintId} className="kp-sr-only">
                    {`Режим: ${lower(current.label)}`}
                    {next ? `. Tab — ${lower(next.label)}` : null}
                  </span>
                ) : null}
                <Kbd aria-hidden="true">Esc</Kbd>
              </div>
              <Command.List
                className="t-pal-list kp-pal-list"
                label={label}
                onPointerMove={listNav.onPointerMove}
              >
                {visible.map((group) => (
                  <Command.Group key={group.id} heading={group.heading} className="kp-pal-group">
                    {group.items.length === 0 && group.emptyText ? (
                      <Command.Item
                        value={`${group.id}:empty`}
                        disabled
                        className="t-pi kp-pi kp-pi-note"
                      >
                        {group.emptyText}
                      </Command.Item>
                    ) : null}
                    {group.items.map((item) => (
                      <Command.Item
                        key={item.id}
                        value={item.id}
                        onSelect={() => select(item)}
                        className="t-pi kp-pi"
                        aria-busy={item.loading || undefined}
                      >
                        {item.loading ? (
                          <span className="kp-pi-spin" aria-hidden="true" />
                        ) : item.icon ? (
                          <Icon icon={item.icon} />
                        ) : null}
                        <span className="kp-pi-label">
                          <Highlighted text={item.label} query={highlight} />
                        </span>
                        {item.sub ? (
                          <span className="sub">
                            <Highlighted text={item.sub} query={highlight} />
                          </span>
                        ) : null}
                        {item.shortcut ? (
                          <span className="r" aria-hidden="true">
                            <ShortcutKeys shortcut={item.shortcut} />
                          </span>
                        ) : null}
                      </Command.Item>
                    ))}
                  </Command.Group>
                ))}
                {allResults ? (
                  <Command.Item
                    value={allResults.id}
                    onSelect={() => select(allResults)}
                    className="t-pi kp-pi kp-pi-all"
                  >
                    {allResults.icon ? <Icon icon={allResults.icon} /> : null}
                    <span className="kp-pi-label">
                      <Highlighted text={allResults.label} query={highlight} />
                    </span>
                    {allResults.sub ? <span className="sub">{allResults.sub}</span> : null}
                  </Command.Item>
                ) : null}
              </Command.List>
              {/* Пояснения — вне списка: в listbox допустимы только пункты и группы. */}
              <div className="kp-pal-status" role="status">
                {searching ? <p className="kp-pal-note">Поиск…</p> : null}
                {!loading && visible.length === 0 && !allResults && empty != null ? (
                  <div className="kp-pal-empty">{empty}</div>
                ) : null}
              </div>
              <div className="t-pal-f" aria-hidden="true">
                <span>
                  <Kbd>↑</Kbd>
                  <Kbd>↓</Kbd> выбрать
                </span>
                <span>
                  <Kbd>↵</Kbd> открыть
                </span>
                <span>
                  <Kbd>Esc</Kbd> закрыть
                </span>
                {next ? (
                  <span>
                    <Kbd>Tab</Kbd> {lower(next.label)}
                  </span>
                ) : null}
              </div>
            </Command>
            <ToastLayerRegion active={open} />
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
